import type { FuelCategory, FuelLeaf, FuelTop, HybridLeaf, LeafCounts } from './types'

/** Display order everywhere: KPI cards, chart stack (bottom → top), legends and tables */
export const FUEL_TOPS: FuelTop[] = ['elbil', 'hybrid', 'diesel', 'bensin', 'annet']

/** «Vis kategori» on Merke / Modell: the categories plus the two hybrid branches */
export const TABLE_CATEGORIES: FuelCategory[] = ['elbil', 'hybrid', 'ladbar', 'ikke_ladbar', 'diesel', 'bensin', 'annet']

export const HYBRID_LEAVES: HybridLeaf[] = ['ladbar', 'ikke_ladbar']

// Categorical slots 1–5 in fixed order, never cycled. Checked with the dataviz
// palette validator against the light and dark chart surfaces (CVD and
// normal-vision separation pass in both); the light steps of aqua, yellow and
// magenta sit under 3:1, so every chart carries a legend and the numbers are
// in the cards and tables beside it.
export const FUEL_COLORS: Record<FuelTop, { light: string; dark: string }> = {
  elbil: { light: '#2a78d6', dark: '#3987e5' },
  hybrid: { light: '#eb6834', dark: '#d95926' },
  diesel: { light: '#1baf7a', dark: '#199e70' },
  bensin: { light: '#eda100', dark: '#c98500' },
  annet: { light: '#e87ba4', dark: '#d55181' },
}

// The Hybrid tab's two lines: orange keeps Hybrid's colour, violet is far from it in every vision type
export const HYBRID_COLORS: Record<HybridLeaf, { light: string; dark: string }> = {
  ladbar: { light: '#eb6834', dark: '#d95926' },
  ikke_ladbar: { light: '#4a3aa7', dark: '#9085e9' },
}

export const fuelColor = (kategori: FuelTop, isDark: boolean) => FUEL_COLORS[kategori][isDark ? 'dark' : 'light']

/** A category's count from leaf counts: hybrid = ladbar + ikke_ladbar */
export function categoryCount(counts: LeafCounts, kategori: FuelCategory): number {
  if (kategori === 'hybrid') return (counts.ladbar ?? 0) + (counts.ikke_ladbar ?? 0)
  return counts[kategori as FuelLeaf] ?? 0
}

/** Share in %, one decimal; null when the whole is 0 (nothing to divide) */
export const shareOf = (part: number, whole: number | null): number | null =>
  whole ? Math.round((part * 1000) / whole) / 10 : null

/** Change in a category's share vs last year, in pp; null without a comparison */
export function shareChangePp(
  count: number, total: number, previousCount: number | null, previousTotal: number | null,
): number | null {
  if (!total || previousCount === null || !previousTotal) return null
  return Math.round(((count * 100) / total - (previousCount * 100) / previousTotal) * 10) / 10
}

/** The categories still shown when electric cars are excluded */
export const visibleTops = (ekskluderElbil: boolean) =>
  ekskluderElbil ? FUEL_TOPS.filter(k => k !== 'elbil') : FUEL_TOPS

/** Merge the applied filter query with the page-level «Ekskluder elbil» switch */
export const withEkskluder = <T extends object>(query: T, ekskluderElbil: boolean) =>
  (ekskluderElbil ? { ...query, ekskluder_elbil: true } : query) as T & { ekskluder_elbil?: boolean }

export const int = (n: number) => n.toLocaleString('nb-NO')
export const pct = (n: number | null) =>
  n === null ? '–' : `${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
export const signed = (n: number | null) =>
  n === null ? '–' : `${n > 0 ? '+' : ''}${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
export const changeColor = (n: number | null) =>
  n === null ? 'text-gray-400' : n > 0 ? 'text-green-600 dark:text-green-400' : n < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'
